/**
 * useScrollLock — locks the body scroll while a modal is open and
 * restores the exact scroll position on close. iOS Safari scrolls
 * the page behind a `overflow:hidden` modal otherwise.
 *
 * P1 Mobile + Live-State sprint (May 13, 2026).
 *
 * Pairs with `.body-modal-open` CSS class declared in index.css.
 * Multiple concurrent modals: a tiny refcount prevents one modal
 * unlocking while another is still open.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   useScrollLock(open);
 */
import { useEffect } from "react";

let _lockCount = 0;
let _savedScrollY = 0;

function applyLock() {
  if (typeof document === "undefined") return;
  if (_lockCount === 0) {
    _savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${_savedScrollY}px`;
    document.body.classList.add("body-modal-open");
  }
  _lockCount += 1;
}

function releaseLock() {
  if (typeof document === "undefined") return;
  _lockCount = Math.max(0, _lockCount - 1);
  if (_lockCount === 0) {
    document.body.classList.remove("body-modal-open");
    document.body.style.top = "";
    // Restore scroll position (instant — `behavior: 'instant'` only
    // honoured by some browsers; raw scrollTo also works on iOS).
    window.scrollTo(0, _savedScrollY);
  }
}

export function useScrollLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;
    applyLock();
    return () => releaseLock();
  }, [locked]);
}

export default useScrollLock;
